import { useMemo } from "react";
import { useEntityDetail } from "../../api/BaseEntityApi";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../api/GenericConstants";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { EntityDetailTableProps } from "../../common/entity/EntityInterfaces";
import { toUppercase } from "../../common/helperfunctions/stringFunctions";
import { NotAvailable, NotSet } from "../../common/misc/UIconstants";
import { Table } from "../../common/panels/Detail/DetailTable";
import { TextEditor } from "../common/TextEditor/TextEditor";
import { LabNotebookEntry } from "../types/LabNotebookEntry";
import { LabNotebookTemplate } from "../types/LabNotebookTemplate";
import { Alert } from "../../common/overlays/Alert/Alert";
import { LoadingWrapper } from "../../common/LoadingWrapper";
import { LinkEntity } from "../../common/misc/LinkEntity/LinkEntity";
import { labNoteBooksConstants } from "../types/LabNotebook";
import { labNotebookExperimentsConstants } from "../types/LabNotebookExperiment";

export interface LabNotebookEntryDetailContentTableProps extends EntityDetailTableProps<"notebookEntries"> {
  includeContent?: boolean;
}

export const LabNotebookEntryDetailContentTable = ({
  entity,
  entityConstants,
  fieldLabels,
  includeContent = false,
}: LabNotebookEntryDetailContentTableProps) => {
  return (
    <>
      <Table noPadding>
        <Table.Head>{toUppercase(entityConstants.entitySingular)} details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.labNotebook}
            content={<LinkEntity entityConstants={labNoteBooksConstants} property={entity.labNotebook} />}
          />

          <Table.Body.RowContent
            title={fieldLabels.labNotebookExperiment}
            content={
              <LinkEntity entityConstants={labNotebookExperimentsConstants} property={entity.labNotebookExperiment} />
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.entryDate}
            content={
              <div className="container_label">
                <div className="container_label_name" style={{ fontWeight: 600 }}>
                  <div style={{ maxWidth: "max-content", width: "100%" }}>
                    <DateTimeRenderer date={entity?.entryDate} includeElapsed={false} includeTime={false} />
                  </div>
                </div>
                <div className="container_label_id">
                  <span style={{ whiteSpace: "nowrap" }}>
                    {toUppercase(entityConstants.entitySingular)}-ID: {entity.id ?? NotAvailable}
                  </span>
                </div>
              </div>
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.name}
            content={
              <div className="container_label">
                <div className="container_label_name">{entity.name ?? NotSet}</div>
              </div>
            }
          />
        </Table.Body>
      </Table>

      {includeContent && (
        <Table>
          <Table.Head>Content</Table.Head>
          <Table.Body>
            <div style={{ padding: "10px", display: "flex", justifyContent: "center" }}>
              <ViewOnlyContent entry={entity} entityTypeId="notebookEntries" />
            </div>
          </Table.Body>
        </Table>
      )}
    </>
  );
};

export const ViewOnlyContent = ({
  entry,
  entityTypeId,
}: {
  entry: LabNotebookEntry | LabNotebookTemplate;
  entityTypeId: GenericEntityConstantsEntities;
}) => {
  const { entityConstants } = genericEntityConstants[entityTypeId];
  const {
    data: entityWithContent,
    isFetching,
    fetchStatus,
    status,
    error,
  } = useEntityDetail<LabNotebookEntry | LabNotebookTemplate>(
    entityConstants.resource,
    entry.id,
    { includeContent: true },
    { enabled: !entry.content, queryKey: [entityConstants.resource, "detail", entry.id, "content"] }
  );
  const content = useMemo(
    () => entry.content ?? entityWithContent?.content,
    [entry.content, entityWithContent?.content]
  );

  if (!content) return <Alert type="warning" message="No content available!" fit centered />;
  return (
    <LoadingWrapper
      status={isFetching ? "loading" : status}
      fetchStatus={isFetching ? "fetching" : fetchStatus}
      error={error}
    >
      <div
        style={{
          width: "210mm",
          minWidth: "210mm",
          maxWidth: "210mm",
          backgroundColor: "var(--white)",
          padding: "40px",
          border: "1px solid var(--gray-300)",
          boxShadow: "1px 1px 1px 1px var(--gray-200)",
        }}
      >
        <TextEditor editable={false} save={() => {}} autosave initialContent={content} generateIds={false} />
      </div>
    </LoadingWrapper>
  );
};
